import { useState, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useI18n } from '../lib/i18n.js';
import { Card, Empty, ErrorBox, Filter, Row, Spinner, Toggle } from '../components/ui.js';
import {
  RuleEditor,
  emptyRule,
  type RuleDraft,
  type RuleOptions,
} from '../components/RuleEditor.js';

interface Catalog {
  events: Array<{
    event: string;
    category: string;
    source: string;
    realtime: boolean;
    defaultEnabled: boolean;
    requiresProduct: string | null;
    note: string | null;
  }>;
  byCategory: Record<string, string[]>;
}

interface Preferences {
  preference: {
    enabled: boolean;
    adminEnabled: boolean;
    adminLocked: boolean;
    mode: 'IMMEDIATE' | 'DIGEST' | 'OFF';
    digestCadence: string;
    quietHours: { enabled: boolean; start: string; end: string; days: number[]; timeZone: string };
    snoozedUntil: string | null;
    eventSettings: Record<string, boolean>;
    lockedEvents: string[];
    minPriorityName: string | null;
    onlyAssignedToMe: boolean;
    onlyReportedByMe: boolean;
    onlyWatched: boolean;
    includedProjects: string[];
    excludedProjects: string[];
    suppressSelfActions: boolean;
  };
  projects: Array<{ projectKey: string; enabled: boolean; adminLocked: boolean }>;
  mutedIssues: Array<{ issueKey: string; mutedUntil: string | null }>;
}

interface Mandates {
  mandates: Array<{
    id: string;
    name: string;
    description: string | null;
    events: string[];
    projects: string[];
    bypassQuietHours: boolean;
    via: 'ALL' | 'TEAM' | 'DIRECT';
  }>;
  teams: Array<{ id: string; name: string }>;
}

/**
 * What an administrator has made compulsory, shown before the switches so the
 * page never looks like it is lying: somebody who turns everything off and
 * still gets messages is owed the reason in the same place.
 */
function MandatesCard(): JSX.Element | null {
  const { t } = useI18n();
  const { data } = useQuery({
    queryKey: ['notify-mandates'],
    queryFn: () => api<Mandates>('/notifications/mandates'),
  });

  if (!data || (data.mandates.length === 0 && data.teams.length === 0)) return null;

  const viaLabel = { ALL: 'mandatory_via_all', TEAM: 'mandatory_via_team', DIRECT: 'mandatory_via_direct' } as const;

  return (
    <Card title={t('notify.mandatory_title')}>
      {data.teams.length > 0 ? (
        <Row
          title={t('notify.my_teams')}
          right={
            <span>
              {data.teams.map((team) => (
                <span key={team.id} className="chip">
                  {team.name}
                </span>
              ))}
            </span>
          }
        />
      ) : null}

      {data.mandates.length === 0 ? null : (
        <div className="note small">{t('notify.mandatory_hint')}</div>
      )}

      {data.mandates.map((mandate) => (
        <Row
          key={mandate.id}
          title={`🔒 ${mandate.name}`}
          subtitle={
            mandate.description ??
            [
              mandate.events.length === 0 ? t('notify.rule_any') : mandate.events.join('، '),
              mandate.projects.length === 0
                ? t('notify.rule_all_projects')
                : mandate.projects.join('، '),
            ].join(' · ')
          }
          right={<span className="chip">{t(`notify.${viaLabel[mandate.via]}`)}</span>}
        />
      ))}
    </Card>
  );
}

export function NotificationsPage(): JSX.Element {
  const { t } = useI18n();
  const client = useQueryClient();
  const [category, setCategory] = useState<string>('assignment');

  const catalog = useQuery({
    queryKey: ['notify-catalog'],
    queryFn: () => api<Catalog>('/notifications/catalog'),
  });

  const prefs = useQuery({
    queryKey: ['notify-prefs'],
    queryFn: () => api<Preferences>('/notifications/preferences'),
  });

  const patch = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api('/notifications/preferences', { method: 'PATCH', body }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['notify-prefs'] }),
  });

  const toggleEvent = useMutation({
    mutationFn: ({ event, enabled }: { event: string; enabled: boolean }) =>
      api(`/notifications/events/${event}`, { method: 'PUT', body: { enabled } }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['notify-prefs'] }),
  });

  const [editing, setEditing] = useState<RuleDraft | null>(null);

  const rules = useQuery({
    queryKey: ['notify-rules'],
    queryFn: () => api<{ rules: RuleDraft[] }>('/notifications/rules'),
  });

  const ruleOptions = useQuery({
    queryKey: ['notify-rule-options'],
    queryFn: () => api<RuleOptions>('/notifications/rule-options'),
    enabled: editing !== null,
  });

  const saveRule = useMutation({
    mutationFn: (draft: RuleDraft) =>
      draft.id
        ? api(`/notifications/rules/${draft.id}`, { method: 'PATCH', body: draft })
        : api('/notifications/rules', { method: 'POST', body: draft }),
    onSuccess: () => {
      setEditing(null);
      void client.invalidateQueries({ queryKey: ['notify-rules'] });
    },
  });

  const removeRule = useMutation({
    mutationFn: (id: string) => api(`/notifications/rules/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setEditing(null);
      void client.invalidateQueries({ queryKey: ['notify-rules'] });
    },
  });

  const snooze = useMutation({
    mutationFn: (minutes: number) =>
      api('/notifications/snooze', { method: 'POST', body: { minutes } }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['notify-prefs'] }),
  });

  if (prefs.isLoading || catalog.isLoading) return <Spinner />;
  if (prefs.error) {
    return (
      <div className="page">
        <ErrorBox error={prefs.error} />
      </div>
    );
  }
  if (!prefs.data || !catalog.data) return <Empty />;

  const preference = prefs.data.preference;
  const locked = preference.adminLocked || !preference.adminEnabled;
  const lockedEvents = new Set(preference.lockedEvents);

  return (
    <div className="page">
      <div className="header">
        <h1>{t('notify.title').replace(/<[^>]+>/g, '')}</h1>
      </div>

      {locked ? <div className="note">🔒 {t('notify.locked_by_admin')}</div> : null}

      <MandatesCard />

      <Card>
        <Toggle
          label={t('notify.master')}
          checked={preference.enabled && preference.adminEnabled}
          disabled={locked}
          onChange={(next) => patch.mutate({ enabled: next })}
        />
        <Row
          title={t('notify.mode')}
          right={
            <select
              value={preference.mode}
              disabled={locked}
              onChange={(event) => patch.mutate({ mode: event.target.value })}
              style={{ width: 'auto' }}
            >
              <option value="IMMEDIATE">{t('notify.mode_immediate')}</option>
              <option value="DIGEST">{t('notify.mode_digest')}</option>
              <option value="OFF">{t('notify.mode_off')}</option>
            </select>
          }
        />
        {preference.mode === 'DIGEST' ? (
          <Row
            title={t('notify.cadence')}
            right={
              <select
                value={preference.digestCadence}
                disabled={locked}
                onChange={(event) => patch.mutate({ digestCadence: event.target.value })}
                style={{ width: 'auto' }}
              >
                <option value="HOURLY">{t('notify.cadence_hourly')}</option>
                <option value="EVERY_4_HOURS">{t('notify.cadence_4h')}</option>
                <option value="DAILY">{t('notify.cadence_daily')}</option>
                <option value="WEEKLY">{t('notify.cadence_weekly')}</option>
              </select>
            }
          />
        ) : null}
      </Card>

      <Card title={t('notify.quiet_hours')}>
        <Toggle
          label={t('notify.quiet_hours')}
          checked={preference.quietHours.enabled}
          disabled={locked}
          hint={preference.quietHours.timeZone}
          onChange={(next) => patch.mutate({ quietHoursEnabled: next })}
        />
        {preference.quietHours.enabled ? (
          <div className="btn-row">
            <input
              type="time"
              value={preference.quietHours.start}
              onChange={(event) => patch.mutate({ quietStart: event.target.value })}
              style={{ width: 'auto' }}
            />
            <input
              type="time"
              value={preference.quietHours.end}
              onChange={(event) => patch.mutate({ quietEnd: event.target.value })}
              style={{ width: 'auto' }}
            />
          </div>
        ) : null}
      </Card>

      <Card title={t('notify.snooze')}>
        {preference.snoozedUntil ? (
          <div className="note">
            {t('notify.snoozed_until', {
              time: new Date(preference.snoozedUntil).toLocaleString(),
            })}
          </div>
        ) : null}
        <div className="btn-row">
          {[30, 60, 240, 480, 1440].map((minutes) => (
            <Filter
              key={minutes}
              label={minutes >= 60 ? `${minutes / 60}h` : `${minutes}m`}
              active={false}
              onClick={() => snooze.mutate(minutes)}
            />
          ))}
          <Filter label={t('common.none')} active={false} onClick={() => snooze.mutate(0)} />
        </div>
      </Card>

      <Card title={t('notify.scope')}>
        <div className="note small">{t('notify.scope_hint')}</div>
        <Toggle
          label={t('home.assigned')}
          checked={preference.onlyAssignedToMe}
          disabled={locked}
          onChange={(next) => patch.mutate({ onlyAssignedToMe: next })}
        />
        <Toggle
          label={t('home.reported')}
          checked={preference.onlyReportedByMe}
          disabled={locked}
          onChange={(next) => patch.mutate({ onlyReportedByMe: next })}
        />
        <Toggle
          label={t('home.watched')}
          checked={preference.onlyWatched}
          disabled={locked}
          onChange={(next) => patch.mutate({ onlyWatched: next })}
        />
        <Toggle
          label={t('notify.suppress_self')}
          checked={preference.suppressSelfActions}
          disabled={locked}
          onChange={(next) => patch.mutate({ suppressSelfActions: next })}
        />
      </Card>

      <Card title={t('notify.rules')}>
        <p className="small muted" style={{ margin: '0 0 10px' }}>
          {t('notify.rules_hint')}
        </p>
        {(rules.data?.rules ?? []).length === 0 ? (
          <Empty text={t('notify.rule_none')} />
        ) : (
          (rules.data?.rules ?? []).map((rule) => (
            <Row
              key={rule.id}
              onClick={() => setEditing(rule)}
              title={rule.name}
              subtitle={describeDraft(rule, t)}
              right={<span className="chip">{rule.enabled ? '✓' : '—'}</span>}
            />
          ))
        )}
        <button
          type="button"
          className="btn btn-block"
          onClick={() => setEditing(emptyRule(t('notify.rule_new')))}
        >
          ＋ {t('notify.rule_new')}
        </button>
        {rules.error ? <ErrorBox error={rules.error} /> : null}
      </Card>

      {editing ? (
        ruleOptions.data ? (
          <RuleEditor
            draft={editing}
            options={ruleOptions.data}
            saving={saveRule.isPending}
            onChange={setEditing}
            onSave={() => saveRule.mutate(editing)}
            onCancel={() => setEditing(null)}
            onDelete={editing.id ? () => removeRule.mutate(editing.id!) : undefined}
          />
        ) : (
          <Spinner />
        )
      ) : null}
      {saveRule.error ? <ErrorBox error={saveRule.error} /> : null}

      <div className="filter-row">
        {Object.keys(catalog.data.byCategory).map((key) => (
          <Filter
            key={key}
            label={key}
            active={category === key}
            onClick={() => setCategory(key)}
          />
        ))}
      </div>

      <Card title={t('notify.defaults')}>
        {catalog.data.events
          .filter((event) => event.category === category)
          .map((event) => (
            <Toggle
              key={event.event}
              label={event.event}
              checked={preference.eventSettings[event.event] ?? event.defaultEnabled}
              disabled={locked || lockedEvents.has(event.event)}
              hint={
                event.realtime
                  ? undefined
                  : `⏱ ${event.note ?? t('notify.not_realtime')}`
              }
              onChange={(next) => toggleEvent.mutate({ event: event.event, enabled: next })}
            />
          ))}
      </Card>

      {prefs.data.mutedIssues.length > 0 ? (
        <Card title={t('notify.muted_issues')}>
          {prefs.data.mutedIssues.map((mute) => (
            <Row
              key={mute.issueKey}
              title={mute.issueKey}
              subtitle={mute.mutedUntil ? new Date(mute.mutedUntil).toLocaleString() : '∞'}
              right={
                <button
                  type="button"
                  className="chip"
                  onClick={async () => {
                    await api(`/issues/${mute.issueKey}/mute`, { method: 'DELETE' });
                    void client.invalidateQueries({ queryKey: ['notify-prefs'] });
                  }}
                >
                  {t('action.unmute')}
                </button>
              }
            />
          ))}
        </Card>
      ) : null}

      {patch.error ? <ErrorBox error={patch.error} /> : null}
      {toggleEvent.error ? <ErrorBox error={toggleEvent.error} /> : null}
    </div>
  );
}

/** One line a person can scan to tell their rules apart. */
function describeDraft(rule: RuleDraft, t: (key: string) => string): string {
  const parts = [
    rule.events.length === 0 ? t('notify.rule_any') : `${rule.events.length}×`,
    rule.projects.length === 0 ? t('notify.rule_all_projects') : rule.projects.join('، '),
  ];
  if (rule.issueTypes.length > 0) parts.push(rule.issueTypes.join('، '));
  if (rule.statuses.length > 0) parts.push(rule.statuses.join('، '));
  if (rule.assigneeMode === 'GROUP') parts.push(rule.assigneeGroups.join('، '));
  else if (rule.assigneeMode !== 'ANYONE') parts.push(t(`notify.rule_${rule.assigneeMode.toLowerCase()}`));
  return parts.join(' · ');
}
